import { useMemo } from "react";
import { useSiteSettings } from "@/hooks/useSiteSettings";

export interface NotificationConfig {
  animationEnabled: boolean;
  particlesEnabled: boolean;
  particlesCount: number;
  animationDurationMs: number;
  soundEnabledDefault: boolean;
  soundVolume: number;
  autoCloseEnabled: boolean;
  autoCloseDelayMs: number;
  showConfettiSpecial: boolean;
  queueShowCount: boolean;
  soundUrl: string | null;
}

export interface NotificationTexts {
  title: string;
  subtitle: string;
  pointsSuffix: string;
  buttonText: string;
  skipAllText: string;
}

const DEFAULT_CONFIG: NotificationConfig = {
  animationEnabled: true,
  particlesEnabled: true,
  particlesCount: 15,
  animationDurationMs: 400,
  soundEnabledDefault: true,
  soundVolume: 0.5,
  autoCloseEnabled: false,
  autoCloseDelayMs: 5000,
  showConfettiSpecial: true,
  queueShowCount: true,
  soundUrl: null,
};

const DEFAULT_TEXTS: NotificationTexts = {
  title: "Conquista Desbloqueada!",
  subtitle: "Parabéns pelo seu progresso!",
  pointsSuffix: "pontos",
  buttonText: "Continuar Explorando",
  skipAllText: "Pular todas",
};

export function useNotificationSettings() {
  const { settings, isLoading, updateSetting, isUpdating } = useSiteSettings();

  const config = useMemo((): NotificationConfig => {
    const configSetting = settings.find((s) => s.setting_key === "notification_config");
    if (!configSetting) return DEFAULT_CONFIG;
    
    try {
      const parsed = JSON.parse(configSetting.setting_value);
      return { ...DEFAULT_CONFIG, ...parsed };
    } catch {
      return DEFAULT_CONFIG;
    }
  }, [settings]);

  const texts = useMemo((): NotificationTexts => {
    const getText = (key: string, fallback: string): string => {
      const setting = settings.find((s) => s.setting_key === key);
      return setting?.setting_value ?? fallback;
    };

    return {
      title: getText("notification_title", DEFAULT_TEXTS.title),
      subtitle: getText("notification_subtitle", DEFAULT_TEXTS.subtitle),
      pointsSuffix: getText("notification_points_suffix", DEFAULT_TEXTS.pointsSuffix),
      buttonText: getText("notification_button_text", DEFAULT_TEXTS.buttonText),
      skipAllText: getText("notification_skip_all_text", DEFAULT_TEXTS.skipAllText),
    };
  }, [settings]);

  const updateConfig = async (newConfig: Partial<NotificationConfig>) => {
    const mergedConfig = { ...config, ...newConfig };
    updateSetting({ key: "notification_config", value: JSON.stringify(mergedConfig) });
  };

  const updateTexts = async (key: string, value: string) => {
    const keyMap: Record<keyof NotificationTexts, string> = {
      title: "notification_title",
      subtitle: "notification_subtitle",
      pointsSuffix: "notification_points_suffix",
      buttonText: "notification_button_text",
      skipAllText: "notification_skip_all_text",
    };
    
    const settingKey = keyMap[key as keyof NotificationTexts];
    if (settingKey) {
      updateSetting({ key: settingKey, value });
    }
  };

  return {
    config,
    texts,
    isLoading,
    isUpdating,
    updateConfig,
    updateTexts,
  };
}
